import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as CalendarEventActions from '../actions/calendar-event.actions';
import * as CalendarEventSelectors from '../selectors/calendar-event.selectors';
import { CalendarEventService } from '@app/schedule/services/calendar-event.service';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { CalendarEvent } from '@app/core/interfaces';
import { of } from 'rxjs';
import { IAppState } from '../state/app.state';

function currentWeekStart(): Date {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function currentWeekEnd(): Date {
  const d = currentWeekStart();
  d.setDate(d.getDate() + 7);
  return d;
}

@Injectable()
export class CalendarEventEffects {
  getAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarEventActions.getCalendarEventsRequest),
      tap(() => console.log('Effect triggered: loadAll')),
      mergeMap(({ startDate, endDate }) =>
        this.calendarEventService.getAllInRange<CalendarEvent[]>(startDate, endDate).pipe(
          map((calendarEvents) =>
            CalendarEventActions.getCalendarEventsSuccess({ calendarEvents }),
          ),
          catchError((error) =>
            of(
              CalendarEventActions.getCalendarEventsFailed({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarEventActions.addCalendarEventRequest),
      tap((eventData) => console.log('Effect triggered: create', eventData)),
      mergeMap(({ eventData }) =>
        this.calendarEventService.create<CalendarEvent>(eventData).pipe(
          map((calendarEvent) =>
            CalendarEventActions.addCalendarEventSuccess({ calendarEvent }),
          ),
          catchError((error) =>
            of(
              CalendarEventActions.addCalendarEventFailed({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarEventActions.updateCalendarEventRequest),
      tap(() => console.log('Effect triggered: update')),
      mergeMap(({ eventData }) =>
        this.calendarEventService.update<CalendarEvent>(eventData).pipe(
          map((updatedEvent) =>
            CalendarEventActions.updateCalendarEventSuccess({
              calendarEvent: updatedEvent,
            }),
          ),
          catchError((error) =>
            of(
              CalendarEventActions.updateCalendarEventFailed({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarEventActions.removeCalendarEventRequest),
      tap(() => console.log('Effect triggered: delete')),
      mergeMap(({ id }) =>
        this.calendarEventService.delete(id).pipe(
          map(() => CalendarEventActions.removeCalendarEventSuccess({ id })),
          catchError((error) =>
            of(
              CalendarEventActions.removeCalendarEventFailed({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  refreshAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CalendarEventActions.addCalendarEventSuccess,
        CalendarEventActions.updateCalendarEventSuccess,
        CalendarEventActions.removeCalendarEventSuccess,
      ),
      withLatestFrom(this.store.select(CalendarEventSelectors.selectViewDateRange)),
      map(([_, { startDate, endDate }]) => {
        const start = startDate ?? currentWeekStart();
        const end = endDate ?? currentWeekEnd();
        return CalendarEventActions.getCalendarEventsRequest({ startDate: start, endDate: end });
      }),
    ),
  );

  constructor(
    private calendarEventService: CalendarEventService,
    private actions$: Actions,
    private store: Store<IAppState>,
  ) {}
}
